import { FormControl, FormLabel, GridItem, Stack, Text } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import {
  FormLabelPendingIndicator,
  isPropertyPending,
} from '~components/ui/FormLabelPendingIndicator';
import { PowerSettingsSection_DisplayFragment } from './__generated__/PowerSettingsSection.graphql';
import { DisplayAutoRestart } from './DisplayAutoRestart';
import { DisplayBatteryManagementToggle } from './DisplayBatteryManagement';
import { DisplaySignalDetectionToggle } from './DisplaySignalDetection';

interface Props {
  display: PowerSettingsSection_DisplayFragment;
}

export function PowerSettingsSection({ display }: Props) {
  const { verifyUserPermissions } = useAuth();

  const isDisplaySettingsUpdateDisabled = !verifyUserPermissions([
    Permission.DisplaySettingsUpdate,
  ]);
  const { t } = useTranslation();

  function showAutoRestart(): boolean {
    const numValue = +(display?.deviceSdk?.version ?? '0');
    return numValue >= 2.07;
  }

  return (
    <GridItem colSpan={{ base: 8, md: 7 }}>
      <Stack direction="column" spacing="10">
        {!isNil(display.signalDetection?.reported) && (
          <FormControl>
            <FormLabel>
              {t('signalDetection')}
              <FormLabelPendingIndicator
                isPending={isPropertyPending(display.recommendedSettings)}
              />
            </FormLabel>

            <Text color="gray.500" mb="2">
              {t('signalDetectionText')}
            </Text>

            <DisplaySignalDetectionToggle
              display={display}
              isDisabled={isDisplaySettingsUpdateDisabled}
            />
          </FormControl>
        )}
        {!isNil(display.deviceSdk?.version) && showAutoRestart() && (
          <DisplayAutoRestart display={display} />
        )}
        {!isNil(display.battery?.reported) && (
          <DisplayBatteryManagementToggle
            display={display}
            isDisabled={isDisplaySettingsUpdateDisabled}
          />
        )}
      </Stack>
    </GridItem>
  );
}

PowerSettingsSection.graphql = {
  fragments: {
    PowerSettingsSection_display: gql`
      fragment PowerSettingsSection_display on Display {
        id
        deviceSdk {
          sdk
          version
        }
        power {
          reportedAt
          desired
        }
        ...DisplaySignalDetectionToggle_display
        ...usePowerToggle_display
        ...DisplayBatteryManagementToggle_display
      }
    `,
  },
};
